<template>
    <div>
        <PageDescriptionEditor page-title="Authors"
            default-description="Discover the talented writers behind our collection of books" />

        <div class="flex justify-between items-center mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">Authors Management</h2>
            <div class="flex gap-3">
                <button @click="openCreateFromBooksModal"
                    class="px-6 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors font-marketing">
                    Create from Books
                </button>
                <button @click="openMergeModal"
                    class="px-6 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors font-marketing">
                    Merge Authors
                </button>
            </div>
        </div>

        <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-4">{{ editingAuthor ? 'Edit' : 'Add' }} Author</h3>
            <form @submit.prevent="saveAuthor" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block font-body text-body mb-1">Name</label>
                        <input v-model="formData.name" type="text" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                    </div>
                    <div>
                        <label class="block font-body text-body mb-1">Location</label>
                        <input v-model="formData.location" type="text"
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-body mb-1">Author Image</label>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <input ref="fileInput" type="file" accept="image/*,.heic,.heif"
                                    @change="handleFileSelect" class="hidden" />
                                <button type="button" @click="() => fileInput?.click()"
                                    class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-body text-body">
                                    Upload Image
                                </button>
                                <input v-model="formData.image_url" type="url" placeholder="Or enter image URL"
                                    class="flex-1 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                            </div>
                            <div v-if="imagePreview || (formData.image_url && !uploading)" class="mt-2 space-y-3">
                                <div class="flex gap-3 items-start">
                                    <div :style="getImageContainerStyle()"
                                        class="border border-creme-300 overflow-hidden bg-white flex items-center justify-center">
                                        <img :src="imagePreview || formData.image_url" alt="Preview"
                                            :style="getImageStyle()" class="w-full h-full object-cover" />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <button type="button" @click="loadImageForCropping"
                                            :disabled="showCropModal || loadingCropImage"
                                            :class="['px-4 py-2 rounded-md transition-colors font-marketing font-semibold text-sm', (showCropModal || loadingCropImage) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-accent text-white hover:bg-green-700']">
                                            {{ loadingCropImage ? 'Loading...' : 'Crop Image' }}
                                        </button>
                                        <button type="button" @click="clearImage"
                                            class="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-marketing">
                                            Remove Image
                                        </button>
                                    </div>
                                </div>
                                <div v-if="formData.image_crop_dimensions && (formData.image_crop_dimensions.width > 0 || formData.image_crop_dimensions.height > 0)"
                                    class="space-y-2">
                                    <div class="text-xs text-gray-600 font-sans">
                                        Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                                            Math.round(formData.image_crop_dimensions.height) }}px
                                    </div>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs font-marketing mb-1">Width (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.width" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-sm bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                                        </div>
                                        <div>
                                            <label class="block text-xs font-marketing mb-1">Height (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.height" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-sm bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                                        </div>
                                    </div>
                                    <div class="p-3 bg-creme-100 rounded-md border border-creme-300">
                                        <p class="text-xs font-marketing font-semibold text-primary mb-2">True-to-Scale
                                            Preview:</p>
                                        <div class="flex items-center justify-center">
                                            <div :style="getTrueToScalePreviewStyle()"
                                                class="border-2 border-gray-400 bg-white overflow-hidden shadow-sm">
                                                <img :src="imagePreview || formData.image_url" alt="True scale preview"
                                                    class="w-full h-full object-cover" />
                                            </div>
                                        </div>
                                        <p class="text-xs text-gray-500 font-sans mt-2 text-center">This is how the
                                            image will appear at actual size</p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="uploading" class="text-sm text-gray-600 font-marketing">
                                Uploading...
                            </div>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <label class="block text-sm font-marketing mb-1">Description</label>
                        <textarea v-model="formData.description" rows="4"
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"></textarea>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button type="submit"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
                        {{ editingAuthor ? 'Update' : 'Create' }}
                    </button>
                    <button type="button" @click="cancelForm"
                        class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-marketing">
                        Cancel
                    </button>
                </div>
            </form>
        </div>

        <div class="mb-4 flex justify-end">
            <button @click="showForm = true; editingAuthor = null"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
                Add New Author
            </button>
        </div>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="w-full overflow-x-auto">
                <table class="w-full min-w-max">
                    <thead class="bg-green-700 text-white">
                        <tr>
                            <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Name</th>
                            <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Location</th>
                            <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Books</th>
                            <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="authors.length === 0" class="border-b border-creme-200">
                            <td colspan="4" class="px-4 py-8 text-center text-gray-500 font-marketing">
                                No authors found. Click "Add New Author" to create one.
                            </td>
                        </tr>
                        <tr v-for="author in authors" :key="author.id"
                            class="border-b border-creme-200 hover:bg-creme-50">
                            <td class="px-4 py-3 font-marketing text-gray-900">
                                <div class="flex items-center gap-3">
                                    <img v-if="author.image_url" :src="author.image_url" :alt="author.name"
                                        class="w-10 h-10 object-cover" />
                                    <span>{{ author.name }}</span>
                                </div>
                            </td>
                            <td class="px-4 py-3 font-marketing text-gray-900">{{ author.location || '-' }}</td>
                            <td class="px-4 py-3 font-marketing text-gray-900">{{ author.book_count || 0 }}</td>
                            <td class="px-4 py-3 whitespace-nowrap">
                                <button @click="editAuthor(author)"
                                    class="text-green-700 hover:text-green-800 mr-3 font-marketing">Edit</button>
                                <button @click="viewAuthorBooks(author)"
                                    class="text-blue-700 hover:text-blue-800 mr-3 font-marketing">View Books</button>
                                <button @click="deleteAuthor(author.id)"
                                    class="text-red-600 hover:text-red-700 font-marketing">Delete</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadAuthors(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-marketing text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadAuthors(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showBooksModal" content-class="max-w-2xl max-h-[80vh]">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-2xl font-marketing font-bold text-primary">
                    Books by {{ selectedAuthor?.name }}
                </h3>
                <button @click="showBooksModal = false"
                    class="text-gray-500 hover:text-gray-700 font-marketing">Close</button>
            </div>
            <div v-if="authorBooks.length === 0" class="text-gray-500 font-marketing">
                No books linked to this author yet.
            </div>
            <div v-else class="space-y-2">
                <div v-for="book in authorBooks" :key="book.id"
                    class="flex justify-between items-center p-3 border border-creme-200 rounded-md">
                    <span class="font-marketing text-gray-900">{{ book.title }}</span>
                    <button @click="unlinkBook(book.id)"
                        class="text-red-600 hover:text-red-700 font-marketing text-sm">Unlink</button>
                </div>
            </div>
        </BaseModal>

        <BaseModal v-model="showCreateFromBooksModal" content-class="max-w-3xl max-h-[80vh]">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-2xl font-marketing font-bold text-primary">
                    Create Authors from Books
                </h3>
                <button @click="closeCreateFromBooksModal"
                    class="text-gray-500 hover:text-gray-700 font-marketing">Close</button>
            </div>
            <div v-if="loadingBookAuthors" class="text-gray-500 font-marketing">
                Loading book authors...
            </div>
            <div v-else-if="bookAuthors.length === 0" class="text-gray-500 font-marketing">
                All book authors have been added to the authors table.
            </div>
            <div v-else class="space-y-4">
                <div class="mb-4">
                    <div class="flex items-center border-b-2 border-gray-300 pb-0">
                        <input type="text" v-model="bookAuthorSearch" placeholder="Search book authors..."
                            class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-base font-sans mr-4" />
                        <div class="flex items-center">
                            <button v-if="bookAuthorSearch.trim()" type="button" @click="clearBookAuthorSearch"
                                class="p-2 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                                aria-label="Clear search">
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <button type="button" @click="handleBookAuthorSearch"
                                class="p-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                                aria-label="Search">
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="space-y-2 max-h-96 overflow-y-auto">
                    <div v-for="bookAuthor in filteredBookAuthors" :key="bookAuthor.name"
                        class="flex items-center justify-between p-3 border border-creme-200 rounded-md hover:bg-creme-50">
                        <div>
                            <div class="font-marketing text-gray-900">{{ bookAuthor.name }}</div>
                            <div class="text-sm text-gray-600 font-sans">{{ bookAuthor.book_count }} book(s)</div>
                        </div>
                        <button @click="createAuthorFromBook(bookAuthor)"
                            class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm">
                            Create Author
                        </button>
                    </div>
                </div>
            </div>
        </BaseModal>

        <BaseModal v-model="showMergeModal" content-class="max-w-2xl">
            <div class="flex justify-between items-center mb-6">
                <h3 class="text-2xl font-marketing font-bold text-primary">
                    Merge Authors
                </h3>
                <button @click="closeMergeModal"
                    class="text-gray-500 hover:text-gray-700 font-marketing text-xl">×</button>
            </div>

            <p class="text-sm text-gray-600 font-sans mb-6">
                Select authors to merge (at least 2). The first selected author will be kept, others will be merged
                into it.
            </p>

            <div class="mb-4">
                <label class="block mb-2 font-marketing font-semibold">Author 1:</label>
                <select v-model="author1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                    <option value="">Select author 1...</option>
                    <option v-for="author in authors" :key="author.id" :value="author.id"
                        :disabled="author.id === author2">
                        {{ author.name }}
                    </option>
                </select>
            </div>

            <div class="mb-6">
                <label class="block mb-2 font-marketing font-semibold">Author 2:</label>
                <select v-model="author2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                    <option value="">Select author 2...</option>
                    <option v-for="author in authors" :key="author.id" :value="author.id"
                        :disabled="author.id === author1">
                        {{ author.name }}
                    </option>
                </select>
            </div>

            <div v-if="!author1 || !author2" class="text-yellow-600 font-marketing mb-4">
                Please select both authors to merge.
            </div>
            <div v-else-if="author1 === author2" class="text-yellow-600 font-marketing mb-4">
                Please select two different authors.
            </div>

            <div v-if="author1 && author2 && author1 !== author2"
                class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
                <h4 class="font-marketing font-semibold text-primary mb-3">Merge Preview</h4>
                <div class="space-y-3">
                    <div class="flex items-start gap-3">
                        <div class="flex-1">
                            <div class="text-sm font-marketing font-semibold text-gray-700 mb-1">Author 1 (Will be
                                kept):</div>
                            <div class="font-sans text-gray-900">{{ getAuthorById(author1)?.name || 'Unknown' }}
                            </div>
                            <div class="text-xs text-gray-600 font-sans mt-1">
                                {{ getAuthorById(author1)?.book_count || 0 }} {{ (getAuthorById(author1)?.book_count
                                    || 0) === 1 ? 'book' : 'books' }}
                                <span v-if="getAuthorById(author1)?.location" class="ml-2">• {{
                                    getAuthorById(author1)?.location }}</span>
                            </div>
                        </div>
                        <span class="text-xs bg-green-700 text-white px-2 py-1 rounded font-marketing">Kept</span>
                    </div>

                    <div class="flex items-center justify-center text-gray-400">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    <div class="flex items-start gap-3">
                        <div class="flex-1">
                            <div class="text-sm font-marketing font-semibold text-gray-700 mb-1">Author 2 (Will be
                                merged into Author 1):</div>
                            <div class="font-sans text-gray-900">{{ getAuthorById(author2)?.name || 'Unknown' }}
                            </div>
                            <div class="text-xs text-gray-600 font-sans mt-1">
                                {{ getAuthorById(author2)?.book_count || 0 }} {{ (getAuthorById(author2)?.book_count
                                    || 0) === 1 ? 'book' : 'books' }}
                                <span v-if="getAuthorById(author2)?.location" class="ml-2">• {{
                                    getAuthorById(author2)?.location }}</span>
                            </div>
                        </div>
                        <span class="text-xs bg-red-700 text-white px-2 py-1 rounded font-marketing">Deleted</span>
                    </div>

                    <div class="mt-4 pt-3 border-t border-creme-300">
                        <div class="text-sm font-marketing font-semibold text-primary mb-1">Result:</div>
                        <div class="font-sans text-gray-900">
                            <strong>{{ getAuthorById(author1)?.name || 'Unknown' }}</strong> will have
                            <strong>{{ (getAuthorById(author1)?.book_count || 0) +
                                (getAuthorById(author2)?.book_count || 0) }} books</strong>
                            ({{ getAuthorById(author1)?.book_count || 0 }} existing + {{
                                getAuthorById(author2)?.book_count || 0 }} from {{ getAuthorById(author2)?.name ||
                                'Author 2' }})
                        </div>
                        <div class="text-xs text-red-600 font-sans mt-2">
                            <strong>{{ getAuthorById(author2)?.name || 'Author 2' }}</strong> will be permanently
                            deleted
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex gap-2 justify-end">
                <button @click="closeMergeModal"
                    class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-marketing">
                    Cancel
                </button>
                <button @click="performMerge" :disabled="!author1 || !author2 || author1 === author2"
                    class="px-6 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors font-marketing disabled:bg-gray-400 disabled:cursor-not-allowed">
                    Merge Authors
                </button>
            </div>
        </BaseModal>

        <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
            <div class="mb-4">
                <h3 class="text-xl font-marketing font-bold text-primary">Crop Author Image</h3>
            </div>
            <div v-if="loadingCropImage" class="mb-4 flex items-center justify-center" style="height: 700px;">
                <Loader size="large" />
            </div>
            <div v-else-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
                <Cropper :key="`cropper-${cropModalKey}-${cropImageSrc}`" ref="cropper" :src="cropImageSrc"
                    :default-boundaries="'fill'" :stencil-props="{
                        aspectRatio: null
                    }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
                <p class="text-gray-500 font-sans">No image selected</p>
            </div>

            <div v-if="cropResult && cropResult.coordinates"
                class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
                <h4 class="text-sm font-marketing font-semibold text-primary mb-3">Preview</h4>
                <div class="flex items-center justify-center">
                    <div class="flex flex-col items-center">
                        <div :style="getPreviewStyle(cropResult.coordinates)"
                            class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden flex items-center justify-center">
                            <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview"
                                class="w-full h-full object-cover" />
                        </div>
                        <p class="text-xs text-gray-500 font-sans mt-1">{{ Math.round(cropResult.coordinates.width) }} ×
                            {{ Math.round(cropResult.coordinates.height) }}px</p>
                    </div>
                </div>
            </div>

            <div class="flex gap-3 justify-end">
                <button @click="closeCropModal"
                    class="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                    Cancel
                </button>
                <button @click="applyCrop"
                    class="px-6 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                    Apply Crop
                </button>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { adminService } from '../../services/admin'
import { API_BASE_URL } from '../../config'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop, watchAndScroll } = useScrollToTop()
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import BaseModal from './BaseModal.vue'

const authors = ref([])
const showForm = ref(false)

// Watch showForm and scroll to top when it becomes true
watchAndScroll(showForm)

const editingAuthor = ref(null)
const imagePreview = ref('')
const uploading = ref(false)
const fileInput = ref(null)
const showBooksModal = ref(false)
const selectedAuthor = ref(null)
const authorBooks = ref([])
const showCreateFromBooksModal = ref(false)
const bookAuthors = ref([])
const loadingBookAuthors = ref(false)
const bookAuthorSearch = ref('')
const showMergeModal = ref(false)
const author1 = ref(null)
const author2 = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)

const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const imageNaturalSize = ref({ width: 0, height: 0 })
const loadingCropImage = ref(false)
const cropModalKey = ref(0)

const formData = ref({
    name: '',
    image_url: '',
    description: '',
    location: '',
    image_crop_dimensions: null
})

const loadAuthors = async (page = 1) => {
    try {
        const data = await adminService.getAuthors({ page, limit: limit.value })
        if (data.pagination) {
            authors.value = data.authors || []
            pagination.value = data.pagination
            currentPage.value = page
        } else {
            authors.value = Array.isArray(data) ? data : []
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load authors:', error)
        authors.value = []
        pagination.value = null
    }
}

const editAuthor = (author) => {
    editingAuthor.value = author
    formData.value = {
        name: author.name,
        image_url: author.image_url || '',
        description: author.description || '',
        location: author.location || '',
        image_crop_dimensions: author.image_crop_dimensions || null
    }
    imagePreview.value = ''
    showForm.value = true
}

const cancelForm = () => {
    showForm.value = false
    editingAuthor.value = null
    formData.value = {
        name: '',
        image_url: '',
        description: '',
        location: '',
        image_crop_dimensions: null
    }
    imagePreview.value = ''
    uploading.value = false
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    scrollToTop()
}

const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const isImage = file.type.startsWith('image/') ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif')
    if (!isImage) {
        alert('Please select an image file')
        return
    }

    if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB')
        return
    }

    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    try {
        const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
        const convertedFile = await convertHeicForUpload(file)
        selectedFile.value = convertedFile

        const blobUrl = await convertHeicForPreview(file)
        imagePreview.value = blobUrl
    } catch (error) {
        console.error('Failed to convert HEIC:', error)
        selectedFile.value = file
        const blobUrl = URL.createObjectURL(file)
        imagePreview.value = blobUrl
    }
}

const loadImageForCropping = async () => {
    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    const imageUrl = imagePreview.value || formData.value.image_url
    if (!imageUrl) {
        alert('Please upload an image or enter an image URL first')
        return
    }

    loadingCropImage.value = true
    cropResult.value = null
    previewImageUrl.value = ''
    imageNaturalSize.value = { width: 0, height: 0 }

    let blobUrl = imagePreview.value

    try {
        if (!blobUrl || !blobUrl.startsWith('blob:')) {
            let blob
            if (imageUrl.includes('storage.googleapis.com')) {
                const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
                const response = await fetch(proxyUrl, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            } else {
                const response = await fetch(imageUrl)
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            }

            blobUrl = URL.createObjectURL(blob)
            imagePreview.value = blobUrl
        }

        cropImageSrc.value = blobUrl
        selectedFile.value = null
        cropModalKey.value++
        showCropModal.value = true
        await nextTick()
    } catch (error) {
        console.error('Failed to load image for cropping:', error)
        alert('Failed to load image. Please check the image URL or try uploading the image again.')
    } finally {
        loadingCropImage.value = false
    }
}

const closeCropModal = () => {
    const fileToUpload = selectedFile.value

    if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc.value)
    }
    showCropModal.value = false
    cropResult.value = null
    cropImageSrc.value = ''
    previewImageUrl.value = ''
    imageNaturalSize.value = { width: 0, height: 0 }
    loadingCropImage.value = false
    cropper.value = null

    if (fileToUpload && !formData.value.image_url) {
        imagePreview.value = ''
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    } else if (!formData.value.image_url) {
        imagePreview.value = ''
    }

    selectedFile.value = null
}

const onCropperReady = () => {
    if (cropper.value) {
        const result = cropper.value.getResult()
        if (result?.imageSize) {
            imageNaturalSize.value = {
                width: result.imageSize.width,
                height: result.imageSize.height
            }
        }
    }
}

const onCropChange = ({ coordinates, canvas }) => {
    cropResult.value = { coordinates, canvas }
    if (canvas) {
        previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
    }
}

const applyCrop = async () => {
    if (!cropper.value || !cropResult.value) {
        alert('Please select a crop area by adjusting the crop box')
        return
    }

    try {
        const { coordinates, canvas } = cropResult.value

        if (!coordinates || !coordinates.width || !coordinates.height) {
            alert('Please select a crop area')
            return
        }

        formData.value.image_crop_dimensions = {
            width: coordinates.width,
            height: coordinates.height
        }

        if (canvas) {
            uploading.value = true

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert('Failed to create cropped image')
                    uploading.value = false
                    return
                }

                try {
                    const currentImageUrl = formData.value.image_url || imagePreview.value
                    let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
                    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
                    fileName = `${nameWithoutExt}.jpg`

                    const formDataUpload = new FormData()
                    formDataUpload.append('file', blob, fileName)

                    const uploadedUrl = await adminService.uploadImage(formDataUpload)
                    if (uploadedUrl) {
                        formData.value.image_url = uploadedUrl
                        imagePreview.value = uploadedUrl

                        if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
                            URL.revokeObjectURL(cropImageSrc.value)
                        }

                        uploading.value = false
                        showCropModal.value = false
                        selectedFile.value = null
                        cropResult.value = null
                    } else {
                        throw new Error('No URL returned from server')
                    }
                } catch (error) {
                    console.error('Failed to upload cropped image:', error)
                    const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
                    alert(errorMessage)
                    uploading.value = false
                }
            }, 'image/jpeg', 0.95)
        } else {
            showCropModal.value = false
        }
    } catch (error) {
        console.error('Error applying crop:', error)
        alert('Failed to apply crop. Please try again.')
    }
}

const getPreviewStyle = (coordinates) => {
    if (!coordinates || !coordinates.width || !coordinates.height) {
        return { width: '200px', height: '200px' }
    }

    let displayWidth = coordinates.width
    let displayHeight = coordinates.height

    const MAX_WIDTH = 300
    if (coordinates.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / coordinates.width
        displayWidth = MAX_WIDTH
        displayHeight = coordinates.height * scale
    }

    return {
        width: `${displayWidth}px`,
        height: `${displayHeight}px`
    }
}

const getImageStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxWidth = 200
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            objectFit: 'cover',
            objectPosition: 'center center'
        }
    }
    return {
        maxWidth: '200px',
        height: '192px',
        objectFit: 'cover',
        objectPosition: 'center center'
    }
}

const getImageContainerStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxWidth = 200
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
    return {
        width: '200px',
        height: '192px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}

const getTrueToScalePreviewStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxPreviewWidth = 400
        let displayWidth = width
        let displayHeight = height

        if (width > maxPreviewWidth) {
            const scale = maxPreviewWidth / width
            displayWidth = maxPreviewWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`
        }
    }
    return {
        width: '200px',
        height: '200px'
    }
}

const updatePreviewDimensions = () => {
    if (!formData.value.image_crop_dimensions) {
        formData.value.image_crop_dimensions = { width: 0, height: 0 }
    }
}

const Math = window.Math

const clearImage = () => {
    formData.value.image_url = ''
    formData.value.image_crop_dimensions = null
    imagePreview.value = ''
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const saveAuthor = async () => {
    try {
        // First, upload the blob image if there's a selected file that hasn't been uploaded yet
        if (selectedFile.value && imagePreview.value && imagePreview.value.startsWith('blob:')) {
            uploading.value = true
            try {
                const formDataUpload = new FormData()
                formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
                const uploadedUrl = await adminService.uploadImage(formDataUpload)
                formData.value.image_url = uploadedUrl
                imagePreview.value = uploadedUrl
                selectedFile.value = null
            } catch (error) {
                console.error('Failed to upload image:', error)
                alert('Failed to upload image. Please try again.')
                uploading.value = false
                return
            } finally {
                uploading.value = false
            }
        }

        const data = { ...formData.value }

        if (data.image_url && data.image_crop_dimensions &&
            data.image_crop_dimensions.width > 0 && data.image_crop_dimensions.height > 0) {
            try {
                const { resizeImage } = await import('../../utils/imageResize.js')
                const resizedBlob = await resizeImage(
                    data.image_url,
                    data.image_crop_dimensions.width,
                    data.image_crop_dimensions.height
                )
                const formDataUpload = new FormData()
                formDataUpload.append('file', resizedBlob, 'resized-image.jpg')
                const resizedUrl = await adminService.uploadImage(formDataUpload)
                data.image_url = resizedUrl
            } catch (error) {
                console.error('Failed to resize image:', error)
            }
        }

        if (editingAuthor.value) {
            await adminService.updateAuthor(editingAuthor.value.id, data)
        } else {
            await adminService.createAuthor(data)
        }

        await loadAuthors(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save author:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Failed to save author. Please try again.'
        alert(errorMessage)
    }
}

const deleteAuthor = async (id) => {
    if (!confirm('Are you sure you want to delete this author?')) return

    try {
        await adminService.deleteAuthor(id)
        await loadAuthors(currentPage.value)
    } catch (error) {
        console.error('Failed to delete author:', error)
        alert('Failed to delete author. Please try again.')
    }
}

const viewAuthorBooks = async (author) => {
    selectedAuthor.value = author
    try {
        authorBooks.value = await adminService.getAuthorBooks(author.id)
        showBooksModal.value = true
    } catch (error) {
        console.error('Failed to load author books:', error)
        alert('Failed to load author books.')
    }
}

const unlinkBook = async (bookId) => {
    if (!confirm('Are you sure you want to unlink this book from the author?')) return

    try {
        await adminService.unlinkBookFromAuthor(bookId, selectedAuthor.value.id)
        await viewAuthorBooks(selectedAuthor.value)
    } catch (error) {
        console.error('Failed to unlink book:', error)
        alert('Failed to unlink book.')
    }
}

const loadBookAuthors = async () => {
    loadingBookAuthors.value = true
    try {
        bookAuthors.value = await adminService.getBookAuthors()
    } catch (error) {
        console.error('Failed to load book authors:', error)
        bookAuthors.value = []
    } finally {
        loadingBookAuthors.value = false
    }
}

const handleBookAuthorSearch = () => {
    // Search is handled reactively by filteredBookAuthors computed property
}

const filteredBookAuthors = computed(() => {
    if (!bookAuthorSearch.value.trim()) {
        return bookAuthors.value
    }
    const query = bookAuthorSearch.value.toLowerCase()
    return bookAuthors.value.filter(author =>
        author.name.toLowerCase().includes(query)
    )
})

const createAuthorFromBook = async (bookAuthor) => {
    if (!confirm(`Create author "${bookAuthor.name}" and link ${bookAuthor.book_count} book(s)?`)) return

    try {
        await adminService.createAuthorFromBook({
            author_name: bookAuthor.name,
            image_url: null,
            description: null,
            location: null
        })
        await loadAuthors(currentPage.value)
        await loadBookAuthors()
        alert('Author created successfully and books linked!')
    } catch (error) {
        console.error('Failed to create author from book:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Failed to create author. Please try again.'
        alert(errorMessage)
    }
}

const closeCreateFromBooksModal = () => {
    showCreateFromBooksModal.value = false
    bookAuthorSearch.value = ''
}

const openCreateFromBooksModal = async () => {
    showCreateFromBooksModal.value = true
    await loadBookAuthors()
}

const getAuthorById = (authorId) => {
    return authors.value.find(a => a.id === authorId)
}

const openMergeModal = () => {
    showMergeModal.value = true
    author1.value = null
    author2.value = null
}

const closeMergeModal = () => {
    showMergeModal.value = false
    author1.value = null
    author2.value = null
}

const performMerge = async () => {
    if (!author1.value || !author2.value) {
        alert('Please select both authors to merge.')
        return
    }

    if (author1.value === author2.value) {
        alert('Please select two different authors.')
        return
    }

    const targetAuthor = authors.value.find(a => a.id === author1.value)
    const sourceAuthor = authors.value.find(a => a.id === author2.value)

    if (!targetAuthor || !sourceAuthor) {
        alert('Author not found.')
        return
    }

    if (!confirm(`Merge "${sourceAuthor.name}" into "${targetAuthor.name}"? This cannot be undone.`)) {
        return
    }

    try {
        await adminService.mergeAuthors([author2.value], author1.value)
        await loadAuthors(currentPage.value)
        closeMergeModal()
        alert('Authors merged successfully!')
    } catch (error) {
        console.error('Failed to merge authors:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Failed to merge authors. Please try again.'
        alert(errorMessage)
    }
}

onMounted(() => {
    loadAuthors(1)
})
</script>

<style scoped></style>
